import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import OutsideClick from '../../../functions/OutsideClick';
import { readPreference, usePopoverPlacement, writePreference } from '../../../functions/popover';
import { CustomColor } from './CustomColor';

import './styles.scss';

// Paint's palette: dark and strong colours above, their light counterparts below.
const STANDARD_COLORS = [
    '#000000', '#7F7F7F', '#880015', '#ED1C24', '#FF7F27', '#FFF200', '#22B14C', '#00A2E8', '#3F48CC', '#A349A4',
    '#FFFFFF', '#C3C3C3', '#B97A57', '#FFAEC9', '#FFC90E', '#EFE4B0', '#B5E61D', '#99D9EA', '#7092BE', '#C8BFE7',
];
// As in Paint, a row of colours of one's own, newest first, kept in this browser.
const CUSTOM_COLORS_KEY = 'customColors';
const MAX_CUSTOM_COLORS = 10;
const PALETTE_WIDTH = 296;
const PALETTE_HEIGHT = 250;

// A colour picker button: value is '#RRGGBB', and onChange gets the colour picked the same way. With noneLabel
// the palette also offers no colour, which onChange gets as null. With defaultColor, the colour of text nobody
// coloured, the palette starts with it. The button shows the colour as a dot, or under icon when one is given,
// as Word's highlighter does.
function ColorPalette({ value, onChange, title, className, noneLabel, icon, defaultColor }) {
    const ref = useRef(null);
    const appColors = useSelector(state => state.colors.colors);
    const [isOpen, setIsOpen] = useState(false);
    // The editor for a colour of one's own, opened from the palette.
    const [editing, setEditing] = useState(false);
    const [custom, setCustom] = useState(() => readPreference(CUSTOM_COLORS_KEY, []));
    // Placed once, when it opens: the editor for one's own colour then grows it away from the button rather than
    // moving it from under the pointer.
    const { above, alignLeft } = usePopoverPlacement(ref, isOpen, PALETTE_WIDTH, PALETTE_HEIGHT);

    useEffect(() => {
        if (!isOpen) setEditing(false);
    }, [isOpen]);
    const current = value?.toUpperCase();

    OutsideClick(ref, () => setIsOpen(false));

    const pick = (color) => {
        onChange(color ? color.toUpperCase() : null);
        setIsOpen(false);
    };

    const addCustom = (color) => {
        const next = [color, ...custom.filter(c => c !== color)].slice(0, MAX_CUSTOM_COLORS);
        setCustom(next);
        writePreference(CUSTOM_COLORS_KEY, next);
        pick(color);
    };

    const firstRow = defaultColor
        ? [defaultColor, ...appColors.map(c => c.color).filter(c => c !== defaultColor)]
        : appColors.map(c => c.color);

    // A colour in more than one place, as black first and among the standard colours, is marked where it first is.
    let marked = false;
    const swatch = (color, key = color) => {
        const selected = !marked && color === current;
        if (selected) marked = true;
        return (
            <button
                type="button"
                key={key}
                title={color}
                className={classNames('palette-swatch', { 'palette-swatch_selected': selected })}
                style={{ background: color }}
                // Picking closes the palette, so the swatch is gone by the time the day window checks whether the
                // press was outside it; it mustn't get there.
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pick(color); }}
            />
        );
    };

    return (
        <div className={classNames('color-palette', className)} ref={ref}>
            <button
                type="button"
                className="color-palette-btn"
                title={title}
                onMouseDown={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
            >
                {icon
                    ? <span className="color-palette-icon">{icon}<i style={{ background: current ?? 'transparent' }} /></span>
                    : <i className="color-palette-current" style={{ background: current }} />}
            </button>

            {isOpen && (
                <div
                    className={classNames('palette', { palette_above: above, 'palette_align-left': alignLeft })}
                    onMouseDown={(e) => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}
                >
                    <div className="palette-title">{title}</div>
                    {noneLabel && (
                        <button
                            type="button"
                            className={classNames('palette-none', { 'palette-none_selected': !current })}
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pick(null); }}
                        >
                            {noneLabel}
                        </button>
                    )}
                    <div className="palette-row">{firstRow.map(color => swatch(color))}</div>
                    <div className="palette-label">Стандартные цвета</div>
                    <div className="palette-grid">{STANDARD_COLORS.map(color => swatch(color))}</div>
                    <div className="palette-label">Свои цвета</div>
                    <div className="palette-grid">
                        {Array.from({ length: MAX_CUSTOM_COLORS }, (_, i) => (custom[i]
                            ? swatch(custom[i])
                            : <span key={`empty-${i}`} className="palette-swatch palette-swatch_empty" />))}
                    </div>
                    {editing
                        ? <CustomColor initial={current} onAdd={addCustom} onCancel={() => setEditing(false)} />
                        : (
                            // The editor takes this button's place under the pointer, so the press stops here
                            // rather than reaching the palette and the day window as one outside them.
                            <button type="button" className="palette-add" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}>
                                + Другой цвет…
                            </button>
                        )}
                </div>
            )}
        </div>
    );
}

export { ColorPalette };
